import {
  SAQ_DOMAIN_LABELS,
  SAQ_DOMAIN_SEQUENCE,
  SAQ_LIMITATION_OPTIONS,
  SAQ_PHYSICAL_ITEMS,
  type SAQResultPayload,
  type SaqDomainKey,
} from '../tests/SAQTest';
import {
  getAssessmentInterpretationOverride,
  resolveAssessmentInterpretationText,
} from '../utils/interpretationOverride';
import QuestionnaireResultHero from '../components/QuestionnaireResultHero';

export const describeSaqScore = (score: number | null) => {
  if (score == null) return 'Sem dados';
  return `Valor legado: ${score.toFixed(1)}/100`;
};

export const getSAQOverallScore = (domainScores?: Partial<Record<SaqDomainKey, number | null>> | null): number | null => {
  if (!domainScores) return null;
  const values = SAQ_DOMAIN_SEQUENCE.map((key) => domainScores[key]).filter((value): value is number => typeof value === 'number');
  if (!values.length) return null;
  return Number((values.reduce((sum, value) => sum + value, 0) / values.length).toFixed(1));
};

export const getSAQSeverityLabel = (overallScore?: number | null): string => {
  if (overallScore == null) return '—';
  return 'Versão adaptada sem classificação SAQ';
};

export const getSAQSeverityTone = (overallScore?: number | null): string => {
  if (overallScore == null) return 'bg-slate-100 text-slate-700';
  return 'bg-slate-100 text-slate-700';
};

export const getSAQInterpretationText = (domainScores?: Partial<Record<SaqDomainKey, number | null>> | null): string => {
  if (!domainScores) return 'Não há dados suficientes para interpretar o SAQ.';
  const worstDomain = SAQ_DOMAIN_SEQUENCE.map((key) => ({ key, score: domainScores[key] ?? null }))
    .filter((item): item is { key: SaqDomainKey; score: number } => typeof item.score === 'number')
    .sort((a, b) => a.score - b.score)[0];

  if (!worstDomain) return 'Não há dados suficientes para interpretar o SAQ.';

  return `Na versão adaptada legada, o menor valor registrado ocorreu no domínio ${SAQ_DOMAIN_LABELS[worstDomain.key].toLowerCase()}. Este resultado não é equivalente ao SAQ-19 ou SAQ-7 oficial e é apresentado sem classificação clínica.`;
};

interface SAQResultRendererProps {
  payload: Partial<SAQResultPayload> | null | undefined;
}

export default function SAQResultRenderer({ payload }: SAQResultRendererProps) {
  const domainScores = payload?.domainScores || {};
  const overallScore = getSAQOverallScore(domainScores);
  const interpretation = resolveAssessmentInterpretationText(payload, getSAQInterpretationText(domainScores));
  const interpretationItems = getAssessmentInterpretationOverride(payload)
    ? []
    : Array.isArray(payload?.interpretation)
      ? payload.interpretation
      : [];

  return (
    <div className="space-y-6">
      <QuestionnaireResultHero
        title="Questionário de angina adaptado — versão legada"
        value={overallScore != null ? overallScore.toFixed(1) : '—'}
        valueColor="#475569"
        valueCaption={overallScore != null ? `${overallScore.toFixed(1)}/100` : 'Pontuação indisponível'}
        classification={getSAQSeverityLabel(overallScore)}
        supportingText="Registro histórico não comparável ao SAQ-19/SAQ-7 oficial."
        interpretation={
          <>
            <p>{interpretation}</p>
            {interpretationItems.length > 0 && (
              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-relaxed text-slate-700">
                <div className="mb-2 font-semibold">Resumo clínico</div>
                <ul className="list-disc space-y-1 pl-5">
                  {interpretationItems.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        }
      />

      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-amber-900">
        A aplicação desta versão foi descontinuada. As respostas são preservadas para auditoria, sem cortes de gravidade ou conversão para versões oficiais licenciadas.
      </div>

      <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-5">
        {SAQ_DOMAIN_SEQUENCE.map((key) => {
          const value = domainScores[key] ?? null;
          return (
            <div key={key} className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
              <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{SAQ_DOMAIN_LABELS[key]}</div>
              <div className="mt-3 text-4xl font-black text-slate-900">{value != null ? value.toFixed(1) : '—'}</div>
              <div className="mt-2 text-sm text-slate-600">{describeSaqScore(value)}</div>
            </div>
          );
        })}
      </div>

      <div className="overflow-hidden rounded-[24px] border border-slate-100 shadow-sm">
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr className="bg-slate-50 text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">
              <th className="px-6 py-4">Atividade</th>
              <th className="px-6 py-4">Resposta selecionada</th>
              <th className="px-6 py-4 text-center">Score do domínio</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {SAQ_PHYSICAL_ITEMS.map((item, index) => {
              const optionIndex = Array.isArray(payload?.physicalAnswers) ? payload.physicalAnswers[index] : -1;
              const option = optionIndex >= 0 ? SAQ_LIMITATION_OPTIONS[optionIndex] : null;
              return (
                <tr key={item} className="hover:bg-slate-50/60">
                  <td className="px-6 py-4 font-medium text-slate-700">{item}</td>
                  <td className="px-6 py-4 text-slate-700">{option?.label ?? 'Não respondido'}</td>
                  <td className="px-6 py-4 text-center font-black text-slate-900">{option?.score ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
